import clsx from 'clsx';
import React, { FC } from 'react';

import { Icon } from '../Icon/Icon';

import styles from './Modal.module.scss';

type ModalProps = {
    isOpen: boolean;
    onClose: () => void;
    className?: string;
    overlayClassName?: string;
    closeClassName?: string;
    maxWidth?: number | string;
    darkMode?: boolean;
    position?: string;
    children?: React.ReactNode;
    uncloseable?: boolean;
    clickOutsideToClose?: boolean;
};

/**
 * Modal component.
 *
 * @param isOpen Whether the modal is currently open.
 * @param onClose Callback to invoke when the modal is closed.
 * @param className The class name to apply to the modal.
 * @param overlayClassName The class name to apply to the overlay.
 * @param closeClassName The class name to apply to the close button.
 * @param maxWidth The maximum width of the modal.
 * @param darkMode Whether to use a dark background color.
 * @param children The content of the modal.
 * @param uncloseable Whether the modal can be closed by clicking outside of it.
 * @param clickOutsideToClose Whether clicking outside of the modal will close it.
 */
export const Modal: FC<ModalProps> = ({
    isOpen,
    onClose,
    className,
    overlayClassName,
    closeClassName,
    maxWidth,
    darkMode,
    children,
    uncloseable,
    clickOutsideToClose = true,
}) => {
    if (!isOpen) return null;

    return (
        <div
            className={clsx(styles.modal_overlay, overlayClassName)}
            onClick={e => {
                e.stopPropagation();
                if (!uncloseable && clickOutsideToClose) onClose();
            }}
        >
            <div
                className={clsx(styles.modal, className, darkMode && styles.dark)}
                style={{ maxWidth }}
                onClick={event => event.stopPropagation()}
            >
                {!uncloseable && (
                    <Icon
                        name="close"
                        className={clsx(styles.close_icon, closeClassName, darkMode && styles.dark_icon)}
                        onClick={e => {
                            e.stopPropagation();
                            onClose();
                        }}
                    />
                )}
                {children}
            </div>
        </div>
    );
};

type StickyModalActionsProps = {
    children: React.ReactNode;
};
export const StickyModalActions: React.FC<StickyModalActionsProps> = ({ children }) => {
    return (
        <div className="flex justify-end gap-2 sticky bottom-0 bg-white bottom-[-24px] m-[-24px] p-6">{children}</div>
    );
};
